/**
 * cleanLogoImage.ts
 *
 * Automatic white background and white corner removal for organization logos.
 * Handles round logos, square logos, and squircles (square with rounded edges)
 * uploaded with white canvas or white JPEG corners.
 *
 * Algorithm:
 * 1. Checks outer perimeter/corners for white or near-white pixels.
 * 2. Runs a fast BFS flood fill starting strictly from the outer boundary inwards.
 *    Any contiguous white pixel connected to the edge is made transparent (alpha = 0).
 * 3. Does NOT touch white elements inside the logo (e.g. white text or stars inside a badge).
 * 4. Applies gentle edge feathering so the transition is smooth and anti-aliased.
 */

const cleanedUrlCache = new Map<string, string>()

/**
 * Removes white/near-white outer background from a canvas in-place.
 * Returns true if any pixels were modified.
 */
export function removeWhiteBackgroundFromCanvas(
  canvas: HTMLCanvasElement,
  threshold = 228,
  feather = true
): boolean {
  const ctx = canvas.getContext('2d')
  if (!ctx) return false

  const w = canvas.width
  const h = canvas.height
  if (w <= 0 || h <= 0) return false

  const imgData = ctx.getImageData(0, 0, w, h)
  const data = imgData.data

  const getIdx = (x: number, y: number) => (y * w + x) * 4

  // Helper to check if a pixel is considered "outer white/near-white"
  const isWhitePixel = (x: number, y: number): boolean => {
    const i = getIdx(x, y)
    const a = data[i + 3]
    if (a < 15) return false // Already transparent
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]

    const minC = Math.min(r, g, b)
    const maxC = Math.max(r, g, b)
    // Must be bright and low saturation (white, off-white, or light gray artifact)
    return minC >= threshold && (maxC - minC) <= 28
  }

  // Quick perimeter check: if corners and midpoints aren't white, skip BFS
  const testPoints = [
    [0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1],
    [Math.floor(w / 2), 0], [Math.floor(w / 2), h - 1],
    [0, Math.floor(h / 2)], [w - 1, Math.floor(h / 2)],
    [Math.floor(w / 4), 0], [Math.floor(3 * w / 4), 0],
    [0, Math.floor(h / 4)], [0, Math.floor(3 * h / 4)]
  ]

  let hasWhiteEdge = false
  for (const [tx, ty] of testPoints) {
    if (isWhitePixel(tx, ty)) {
      hasWhiteEdge = true
      break
    }
  }

  if (!hasWhiteEdge) {
    return false
  }

  // Visited array and queue for BFS flood fill
  const visited = new Uint8Array(w * h)
  const queue = new Int32Array(w * h)
  let qHead = 0
  let qTail = 0

  const pushPixel = (x: number, y: number) => {
    const idx = y * w + x
    if (visited[idx]) return
    visited[idx] = 1
    queue[qTail++] = idx
  }

  // Seed with all perimeter pixels that match white
  for (let x = 0; x < w; x++) {
    if (isWhitePixel(x, 0)) pushPixel(x, 0)
    if (isWhitePixel(x, h - 1)) pushPixel(x, h - 1)
  }
  for (let y = 0; y < h; y++) {
    if (isWhitePixel(0, y)) pushPixel(0, y)
    if (isWhitePixel(w - 1, y)) pushPixel(w - 1, y)
  }

  if (qTail === 0) return false

  let modifiedCount = 0

  // BFS traversal: clears contiguous outer white
  while (qHead < qTail) {
    const curr = queue[qHead++]
    const cx = curr % w
    const cy = Math.floor(curr / w)
    const pIdx = curr * 4

    // Make pixel transparent
    data[pIdx + 3] = 0
    modifiedCount++

    // 4-neighborhood
    if (cx + 1 < w && !visited[curr + 1] && isWhitePixel(cx + 1, cy)) {
      visited[curr + 1] = 1
      queue[qTail++] = curr + 1
    }
    if (cx - 1 >= 0 && !visited[curr - 1] && isWhitePixel(cx - 1, cy)) {
      visited[curr - 1] = 1
      queue[qTail++] = curr - 1
    }
    if (cy + 1 < h && !visited[curr + w] && isWhitePixel(cx, cy + 1)) {
      visited[curr + w] = 1
      queue[qTail++] = curr + w
    }
    if (cy - 1 >= 0 && !visited[curr - w] && isWhitePixel(cx, cy - 1)) {
      visited[curr - w] = 1
      queue[qTail++] = curr - w
    }
  }

  if (modifiedCount === 0) return false

  // Edge feathering / anti-aliasing to remove fringing
  if (feather) {
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const pPos = y * w + x
        const pIdx = pPos * 4
        const alpha = data[pIdx + 3]

        if (alpha > 0) {
          // Check if any adjacent neighbor is transparent
          const neighborTransparent =
            data[getIdx(x + 1, y) + 3] === 0 ||
            data[getIdx(x - 1, y) + 3] === 0 ||
            data[getIdx(x, y + 1) + 3] === 0 ||
            data[getIdx(x, y - 1) + 3] === 0

          if (neighborTransparent) {
            const r = data[pIdx]
            const g = data[pIdx + 1]
            const b = data[pIdx + 2]
            const minC = Math.min(r, g, b)

            // If it's a light fringe pixel, taper alpha
            if (minC >= 200) {
              const fade = Math.max(0, Math.min(1, (minC - 200) / 55))
              data[pIdx + 3] = Math.round(alpha * (1 - fade * 0.85))
            }
          }
        }
      }
    }
  }

  ctx.putImageData(imgData, 0, 0)
  return true
}

/**
 * Cleans an uploaded File by stripping outer white backgrounds and
 * returning a transparent PNG File ready to upload to Supabase storage.
 */
export async function cleanLogoFile(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file

  return new Promise((resolve) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth || img.width
        canvas.height = img.naturalHeight || img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve(file)

        ctx.drawImage(img, 0, 0)
        removeWhiteBackgroundFromCanvas(canvas)

        canvas.toBlob((blob) => {
          if (!blob) return resolve(file)
          const cleanFile = new File([blob], 'logo.png', { type: 'image/png' })
          resolve(cleanFile)
        }, 'image/png')
      } catch (err) {
        console.warn('[cleanLogoFile] fallback:', err)
        resolve(file)
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(file)
    }

    img.src = objectUrl
  })
}

/**
 * Takes an image URL, removes any outer white background or corners in an offscreen canvas,
 * and returns a transparent PNG data URL (or original URL if clean/CORS blocked).
 */
export async function removeWhiteBackgroundFromUrl(url: string): Promise<string> {
  if (!url) return url
  if (cleanedUrlCache.has(url)) return cleanedUrlCache.get(url)!

  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth || img.width
        canvas.height = img.naturalHeight || img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          cleanedUrlCache.set(url, url)
          return resolve(url)
        }

        ctx.drawImage(img, 0, 0)
        const modified = removeWhiteBackgroundFromCanvas(canvas)

        if (modified) {
          const dataUrl = canvas.toDataURL('image/png')
          cleanedUrlCache.set(url, dataUrl)
          resolve(dataUrl)
        } else {
          cleanedUrlCache.set(url, url)
          resolve(url)
        }
      } catch (err) {
        // In case of canvas CORS restriction, fallback to original URL
        cleanedUrlCache.set(url, url)
        resolve(url)
      }
    }

    img.onerror = () => {
      resolve(url)
    }

    img.src = url
  })
}

/**
 * Downloads an existing logo from its URL, cleans white background/corners,
 * and returns an image/png Blob ready to re-upload to Supabase Storage.
 */
export async function convertUrlToCleanPngBlob(url: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth || img.width
        canvas.height = img.naturalHeight || img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve(null)

        ctx.drawImage(img, 0, 0)
        removeWhiteBackgroundFromCanvas(canvas)

        canvas.toBlob((blob) => {
          resolve(blob)
        }, 'image/png')
      } catch (err) {
        console.warn('[convertUrlToCleanPngBlob] failed:', err)
        resolve(null)
      }
    }

    img.onerror = () => resolve(null)
    img.src = url
  })
}
